// src/components/hollo-audio-player.ts
import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { holloAudioPlayerStyles } from "./hollo-audio-player-styles";
import { v4 as uuidv4 } from "uuid";

@customElement("hollo-audio-player")
export class HolloAudioPlayer extends LitElement {
  /** data: or blob: or https: audio URL */
  @property({ type: String }) src = "";
  /** When true, starts playback after the waveform is ready */
  @property({ type: Boolean, reflect: true }) autoplay = false;
  /** Visual height of the waveform (px) */
  @property({ type: Number }) height = 44;
  /** When true, the waveform is for an AI voice */
  @property({ type: Boolean }) isAiVoice = false;

  @property({ type: String }) forMessageId?: string;

  @state() private ready = false;
  @state() private playing = false;
  @state() private cur = 0;
  @state() private dur = 0;

  private audio?: HTMLAudioElement;
  private instanceId: string = uuidv4();
  private onGlobalStop?: (e: Event) => void;
  private onGlobalToggle?: (e: Event) => void;
  private timeUpdateInterval?: number;

  render() {
    const progress = this.dur > 0 ? (this.cur / this.dur) * 100 : 0;
    const isHumanVoice = !this.isAiVoice;

    return html`
      <div
        class="hollo-audio-player ${this.isAiVoice ? "hollo-isAiVoice" : ""}"
      >
        <div class="hollo-wrap" aria-label="Voice message player">
          <button
            class="hollo-pp ${this.playing ? "hollo-pause" : "hollo-play"}"
            @click=${this.toggle}
            aria-label=${this.playing ? "Pause" : "Play"}
          ></button>
          ${isHumanVoice
            ? html`
                <!-- Simple scrub bar for human voice messages -->
                <div
                  class="hollo-scrub-bar"
                  @pointerdown=${this.handleScrubStart}
                  role="slider"
                  aria-valuemin="0"
                  aria-valuemax=${this.dur || 0}
                  aria-valuenow=${this.cur || 0}
                >
                  <div class="hollo-scrub-track"></div>
                  <div
                    class="hollo-scrub-thumb"
                    style="left: ${progress}%"
                  ></div>
                </div>
              `
            : html`
                <!-- Waveform for AI voices (kept for backward compatibility) -->
                <div
                  id="wave"
                  class="hollo-wave"
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax=${this.dur || 0}
                  aria-valuenow=${this.cur || 0}
                ></div>
              `}
          <div class="hollo-time">
            ${isHumanVoice
              ? this.playing || this.cur > 0
                ? this.fmt(this.cur)
                : this.dur > 0
                ? this.fmt(this.dur)
                : this.fmt(this.cur)
              : `${this.fmt(this.cur)} / ${this.fmt(this.dur)}`}
          </div>
        </div>
      </div>
      <audio
        ?hidden=${true}
        .src=${this.src}
        @loadedmetadata=${this.onLoadedMetadata}
        @ended=${this.onEnded}
      ></audio>
    `;
  }

  protected firstUpdated() {
    const audioEl = this.shadowRoot?.querySelector("audio") as HTMLAudioElement;
    if (audioEl) {
      this.audio = audioEl;
      if (this.src) {
        this.audio.load();
      }

      // Set up play/pause event listeners
      this.audio.addEventListener("play", () => {
        this.playing = true;
      });
      this.audio.addEventListener("pause", () => {
        this.playing = false;
      });
    }

    // Set up time update interval
    this.timeUpdateInterval = window.setInterval(() => {
      if (this.audio && this.ready) {
        this.cur = this.audio.currentTime;
        this.requestUpdate();
      }
    }, 100);

    // Listen for global stop to pause this player when others start
    this.onGlobalToggle = (ev: Event) => {
      const { forMessageId } =
        (ev as CustomEvent<{ forMessageId?: string }>).detail || {};
      if (!forMessageId || forMessageId !== this.forMessageId) return;
      if (!this.audio || !this.ready) return;

      // If this is the same voice being clicked again:
      // pause if playing, resume if paused — NO restart.
      if (this.playing) {
        this.audio.pause();
      } else {
        this.audio.play().catch(() => {});
      }
    };
    window.addEventListener("hollo-toggle-audio", this.onGlobalToggle!);

    this.onGlobalStop = (ev: Event) => {
      const anyEv = ev as CustomEvent<{ id?: string }>;
      const srcId = anyEv.detail?.id;
      if (!srcId || srcId === this.instanceId) return;
      try {
        if (this.audio && this.playing) this.audio.pause();
      } catch {}
    };
    window.addEventListener("hollo-stop-audio", this.onGlobalStop);
  }

  protected updated(changed: Map<string, unknown>) {
    if (changed.has("src") && this.audio) {
      this.ready = false;
      this.playing = false;
      this.cur = 0;
      this.dur = 0;
      if (this.src) {
        this.audio.src = this.src;
        this.audio.load();
      }
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    try {
      if (this.audio) {
        this.audio.pause();
        this.audio.src = "";
      }
    } catch {}
    this.audio = undefined;
    if (this.timeUpdateInterval) {
      window.clearInterval(this.timeUpdateInterval);
      this.timeUpdateInterval = undefined;
    }
    if (this.onGlobalStop) {
      window.removeEventListener("hollo-stop-audio", this.onGlobalStop);
      this.onGlobalStop = undefined;
    }
    if (this.onGlobalToggle) {
      window.removeEventListener("hollo-toggle-audio", this.onGlobalToggle);
      this.onGlobalToggle = undefined;
    }
  }

  private onLoadedMetadata = () => {
    if (this.audio) {
      this.ready = true;
      this.dur = this.audio.duration;
      if (this.autoplay) {
        // Let layout settle to avoid blocked autoplay in some browsers
        setTimeout(() => this.audio?.play().catch(() => {}), 0);
      }
    }
  };

  private onEnded = () => {
    this.playing = false;
    this.cur = 0; // Reset progress bar to start when audio finishes
    if (this.audio) {
      this.audio.currentTime = 0; // Reset audio position to start
    }
    this.dispatchEvent(
      new CustomEvent("audio-ended", { bubbles: true, composed: true })
    );
  };

  private toggle = () => {
    if (!this.audio || !this.ready) return;
    if (this.playing) {
      this.audio.pause();
    } else {
      this.audio.play().catch(() => {});
      // Announce globally so other players stop
      try {
        window.dispatchEvent(
          new CustomEvent("hollo-stop-audio", {
            detail: { id: this.instanceId },
          })
        );
      } catch {}
      this.playing = true;
      this.dispatchEvent(
        new CustomEvent("audio-started", { bubbles: true, composed: true })
      );
    }
  };

  private handleScrubStart = (e: PointerEvent) => {
    if (!this.audio || !this.dur) return;
    e.preventDefault();

    const scrubBar = e.currentTarget as HTMLElement;
    const rect = scrubBar.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const clamped = Math.min(Math.max(ratio, 0), 1);
    const time = this.dur * clamped;

    if (this.audio) {
      this.audio.currentTime = time;
      this.cur = time;
    }

    const handleMove = (moveEvent: PointerEvent) => {
      const ratio = (moveEvent.clientX - rect.left) / rect.width;
      const clamped = Math.min(Math.max(ratio, 0), 1);
      const time = this.dur * clamped;
      if (this.audio) {
        this.audio.currentTime = time;
        this.cur = time;
      }
    };

    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp, { once: true });
  };

  private fmt(s: number) {
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const totalSeconds = Math.floor(s);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  static styles = holloAudioPlayerStyles;
}

declare global {
  interface HTMLElementTagNameMap {
    "hollo-audio-player": HolloAudioPlayer;
  }
}
